/* eslint-disable no-nested-ternary */

import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import {
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  takeUntil,
} from 'rxjs/operators';

import { isAuthenticated } from 'actions/user';
import {
  SUBSCRIBE_OPEN_POSITION_SPOT,
  SUBSCRIBE_PRICE_ALERT_COIN_SPOT,
  SUBSCRIBE_SELECTED_PRODUCT_SPOT,
  SUBSCRIBE_SPOT,
  SUBSCRIBE_WALLET_SPOT,
  UNSUBSCRIBE_ALL,
  UNSUBSCRIBE_OPEN_POSITION_SPOT,
  UNSUBSCRIBE_PRICE_ALERT_COIN_SPOT,
  UNSUBSCRIBE_SELECTED_PRODUCT_SPOT,
  UNSUBSCRIBE_SPOT,
  UNSUBSCRIBE_WALLET_SPOT,
} from 'actionTypes/socket';
import {
  HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS,
  IS_INDIAN_EXCHANGE,
} from 'constants/constants';
import { isNotEmpty } from 'ramdax';
import { socketActiveSelector } from 'selectors/socketSelectors';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { updateSelectedCoinSpotPrice } from 'variableStore/actions/priceAlerts';

import {
  updateOpenPositionSpotPrices,
  updateSelectedProductSpot,
  updateWalletSpotPrices,
} from '../variableStore/actions/price-index';
import { getSpotSymbolFromProductSymbol } from './helper';
import { enabledWalletSpotSymbols, wsSubject } from './socket';

const payloadGenerator = (type, name, symbols) => {
  return {
    type,
    payload: {
      channels: [
        {
          name,
          symbols,
        },
      ],
    },
  };
};

const spotChannel = symbols =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'v2/spot_price', symbols),
    () => payloadGenerator('unsubscribe', 'v2/spot_price', symbols),
    message => message.type === 'v2/spot_price'
  );

const subscribeSpotChannel = symbol =>
  wsSubject.multiplex(
    () => payloadGenerator('subscribe', 'v2/spot_price', symbol),
    () => {},
    message => message.type === 'v2/spot_price'
  );

const unsubscribeSpotChannel = symbol =>
  wsSubject.multiplex(
    () => payloadGenerator('unsubscribe', 'v2/spot_price', symbol),
    () => {},
    message => message.type === 'v2/spot_price'
  );

export const unsubscribeSelectedProductSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_SELECTED_PRODUCT_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action =>
      unsubscribeSpotChannel([action.payload]).pipe(
        catchError(err => {
          console.error('UNSUBSCRIBE_SELECTED_PRODUCT_SPOT', err);
          return EMPTY;
        })
      )
    )
  );

export const subscribeSelectedProductSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_SELECTED_PRODUCT_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action =>
      subscribeSpotChannel([action.payload]).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL)),
        catchError(err => {
          console.error('Selected product spot', err);
          return EMPTY;
        })
      )
    ),
    filter(data => {
      const symbol = selectedProductSelector(state$.value)?.spot_index?.symbol;
      return isNotEmpty(data) && symbol === data.s;
    }),
    // map(data => updateSpotMarkData({ symbol: data.s, price: data.p })), // updateChartSpotData
    map(data => updateSelectedProductSpot({ symbol: data.s, price: data.p })),
    ignoreElements()
  );

export const subscribeWalletSpotChannelEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_WALLET_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    // is not a indian exchange and user is authenticated
    filter(() => !IS_INDIAN_EXCHANGE && isAuthenticated(state$.value.user)),
    mergeMap(() => {
      const spotSymbols = enabledWalletSpotSymbols();
      return spotChannel(spotSymbols).pipe(
        takeUntil(action$.ofType(UNSUBSCRIBE_ALL, UNSUBSCRIBE_WALLET_SPOT)),
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateWalletSpotPrices)
  );

export const subscribeOpenPositionsSpotEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_OPEN_POSITION_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      const socketActive = socketActiveSelector(state$.value);
      return !isMinimalUpdateUser && socketActive;
    }),
    mergeMap(action => {
      return subscribeSpotChannel([action.payload]).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateOpenPositionSpotPrices)
  );

export const unsubscribeOpenPositionsSpotEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_OPEN_POSITION_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    filter(action => {
      const selectedProduct = selectedProductSelector(state$.value);
      if (!selectedProduct) return false;
      const symbol = selectedProduct?.spot_index?.symbol;
      if (enabledWalletSpotSymbols().includes(action.payload)) return false;
      if (symbol === action.payload) return false;
      return true;
    }),
    mergeMap(action => {
      return unsubscribeSpotChannel(action.payload).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export const subscribeSpotForHomePageEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(() => {
      const symbols = HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS;
      return subscribeSpotChannel(symbols).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateWalletSpotPrices)
  );

export const unSubscribeSpotForHomePageEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const symbol = action.payload;
      const spotSymbol = getSpotSymbolFromProductSymbol(symbol);
      const symbolToUnsubscribe = HOME_PAGE_ASSETS_FOR_SPOT_SYMBOLS.filter(
        s => s !== spotSymbol
      );
      return unsubscribeSpotChannel(symbolToUnsubscribe).pipe(
        catchError(err => {
          console.error('Wallet spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export const subscribeSpotForSelectedCoinEpic = (action$, state$) =>
  action$.pipe(
    ofType(SUBSCRIBE_PRICE_ALERT_COIN_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const { symbol } = action.payload;
      return subscribeSpotChannel([symbol]).pipe(
        catchError(err => {
          console.error('Selected coin spot channels', err);
          return EMPTY;
        })
      );
    }),
    filter(isNotEmpty),
    map(updateSelectedCoinSpotPrice)
  );

export const unSubscribeSpotForSelectedCoinEpic = (action$, state$) =>
  action$.pipe(
    ofType(UNSUBSCRIBE_PRICE_ALERT_COIN_SPOT),
    filter(() => {
      const isMinimalUpdateUser = state$?.value?.user?.preferences?.minimal_updates;
      return !isMinimalUpdateUser;
    }),
    mergeMap(action => {
      const { symbol } = action.payload;
      return unsubscribeSpotChannel([symbol]).pipe(
        catchError(err => {
          console.error('Selected coin spot channels', err);
          return EMPTY;
        })
      );
    })
  );

export default combineEpics(
  subscribeSelectedProductSpotChannelEpic,
  unsubscribeSelectedProductSpotChannelEpic,
  subscribeWalletSpotChannelEpic,
  subscribeOpenPositionsSpotEpic,
  unsubscribeOpenPositionsSpotEpic,
  subscribeSpotForHomePageEpic,
  unSubscribeSpotForHomePageEpic,
  subscribeSpotForSelectedCoinEpic,
  unSubscribeSpotForSelectedCoinEpic
);
